export default function TermsPage() {
  return (
    <div className="min-h-screen bg-[#F7F7F8] px-4 py-12" data-testid="public-terms-page">
      <div className="max-w-[720px] mx-auto bg-white rounded-md border border-[#E4E4E7] p-8 sm:p-10">
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#09090B]">Terms of Service</h1>
        <p className="text-sm text-[#71717A] mt-2">Last updated: September 8, 2026</p>

        <div className="prose prose-sm max-w-none mt-6 text-[#27272A] space-y-5">
          <section>
            <h2 className="font-heading text-lg font-bold text-[#09090B]">1. Agreement</h2>
            <p>
              These Terms govern access to and use of SprayOps ("the Service"), operated by Olimpo Web Design LLC
              ("we", "us"). By accessing the Service you agree to these Terms. If you do not agree, do not use the
              Service.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-lg font-bold text-[#09090B]">2. What the Service is</h2>
            <p>
              SprayOps is a private business tool that helps a contracting company plan, generate and publish
              marketing content, manage the leads that content produces, and review the performance of its own
              website in Google Search Console and Google Analytics. It is provided to specific client businesses
              and their staff under a separate commercial agreement. It is not offered to the general public and
              there is no self-service signup.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-lg font-bold text-[#09090B]">3. Accounts and access</h2>
            <p>
              Access requires signing in with a Google account authorized by the business that holds the account.
              You are responsible for keeping your sign-in secure and for the activity of the users you invite. We
              may suspend access that appears compromised or that violates these Terms.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-lg font-bold text-[#09090B]">4. Connected accounts</h2>
            <p>
              The Service can connect to third-party accounts that you own or administer, including Google,
              Facebook, Instagram and other social platforms. You represent that you are authorized to connect
              those accounts. Your use of each third-party platform remains governed by that platform's own terms.
              We access those accounts only to provide the features described in the Service and in our Privacy
              Policy, and you can disconnect any account at any time from the Connections screen.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-lg font-bold text-[#09090B]">5. Your content and your data</h2>
            <p>
              The content you create in the Service, the leads submitted through your forms, and the reports
              retrieved from your own Google properties belong to you. We do not sell them, do not share them with
              other clients, and do not use them to train machine learning models. We process them only to operate
              the Service.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-lg font-bold text-[#09090B]">6. Acceptable use</h2>
            <p>
              You agree not to use the Service to publish unlawful, deceptive or infringing content; to impersonate
              another business; to send unsolicited bulk messages; to attempt to access another client's data; or
              to interfere with the operation of the Service or of the platforms it connects to. Content generated
              with the assistance of AI is a draft: you are responsible for reviewing it before it is published
              under your brand.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-lg font-bold text-[#09090B]">7. Availability</h2>
            <p>
              We aim to keep the Service available but do not guarantee uninterrupted operation. Features that
              depend on third-party platforms may stop working if those platforms change or restrict their APIs.
              Maintenance and updates may cause brief interruptions.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-lg font-bold text-[#09090B]">8. Fees</h2>
            <p>
              Access to the Service is governed by the commercial agreement signed between Olimpo Web Design LLC
              and the client business. These Terms do not by themselves create any payment obligation.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-lg font-bold text-[#09090B]">9. No warranty</h2>
            <p>
              The Service is provided "as is" and "as available", without warranties of any kind, express or
              implied, including merchantability, fitness for a particular purpose and non-infringement. We do not
              warrant that content generated by the Service is accurate, complete or suitable for publication
              without review.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-lg font-bold text-[#09090B]">10. Limitation of liability</h2>
            <p>
              To the maximum extent permitted by law, Olimpo Web Design LLC will not be liable for indirect,
              incidental, special, consequential or punitive damages, or for lost profits, revenue, data or
              goodwill, arising out of or related to the Service. Our total aggregate liability arising out of or
              related to the Service will not exceed the amounts paid for the Service in the twelve months
              preceding the event giving rise to the claim.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-lg font-bold text-[#09090B]">11. Termination</h2>
            <p>
              Either party may end the use of the Service as set out in the commercial agreement. On termination we
              delete the stored connection credentials and, on request, export or delete the data held for the
              client business.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-lg font-bold text-[#09090B]">12. Changes to these Terms</h2>
            <p>
              We may update these Terms. The date at the top of this page shows the last update. Continued use of
              the Service after an update means you accept the revised Terms.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-lg font-bold text-[#09090B]">13. Governing law</h2>
            <p>
              These Terms are governed by the laws applicable at the registered seat of Olimpo Web Design LLC, and
              any dispute arising from them will be submitted to the courts having jurisdiction there, without
              prejudice to any mandatory consumer protection rules.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-lg font-bold text-[#09090B]">14. Contact</h2>
            <p>
              Olimpo Web Design LLC — contacto@olimpo.tech —{" "}
              <a href="https://olimpo.tech" target="_blank" rel="noreferrer" className="text-[#0055FF]">
                https://olimpo.tech
              </a>
            </p>
          </section>
        </div>

        <footer className="mt-8 pt-6 border-t border-[#E4E4E7] text-sm text-[#71717A]" data-testid="terms-footer">
          <a href="/" className="hover:text-[#0055FF]" data-testid="terms-footer-home-link">Home</a>
          {" · "}
          <a href="/privacy" className="hover:text-[#0055FF]" data-testid="terms-footer-privacy-link">Privacy Policy</a>
          {" · "}
          <a href="/terms" className="hover:text-[#0055FF]" data-testid="terms-footer-terms-link">Terms of Service</a>
          {" · "}contacto@olimpo.tech
        </footer>
      </div>
    </div>
  );
}
